import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowRight,
  Calendar as CalendarIcon,
  FileSpreadsheet,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  ACCEPTED_UPLOAD_EXTENSIONS,
  ACCEPTED_UPLOAD_MIME,
  REPORTS_BY_SOURCE,
  UPLOAD_SOURCES,
  sourceRequiresYear,
  type UploadReport,
  type UploadSource,
} from "@/lib/uploadConstants";
import { uploadFile } from "@/services/blobApi";

const CURRENT_YEAR = new Date().getUTCFullYear();
const MIN_YEAR = 2000;
const MAX_YEAR = CURRENT_YEAR;

function validateYear(value: string): string {
  const n = Number(value);
  if (!Number.isInteger(n) || n < MIN_YEAR || n > MAX_YEAR) {
    return `Enter a year between ${MIN_YEAR} and ${MAX_YEAR}.`;
  }
  return "";
}

function extensionOf(name: string): string {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx).toLowerCase() : "";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface DataUploadsTabProps {
  onOpenLatest?: (source: UploadSource, report: UploadReport) => void;
}

export function DataUploadsTab({ onOpenLatest }: DataUploadsTabProps = {}) {
  const { toast } = useToast();

  const [source, setSource] = useState<UploadSource | "">("");
  const [report, setReport] = useState<UploadReport | "">("");
  const [year, setYear] = useState<string>(String(new Date().getUTCFullYear()));
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const availableReports = source ? REPORTS_BY_SOURCE[source] : [];

  const requiresYear = source ? sourceRequiresYear(source) : true;
  const yearError = requiresYear ? validateYear(year) : "";
  const canUpload = source && report && !yearError && file && !fileError;

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!source || !report || !file) throw new Error("Missing upload fields");
      return uploadFile({
        source,
        report,
        year: requiresYear ? Number(year) : undefined,
        file,
      });
    },
    onSuccess: () => {
      toast({
        title: "File uploaded",
        description: file ? `${file.name} was uploaded successfully.` : undefined,
        duration: 10000,
      });
      setFile(null);
      setUploadError(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: (e: Error) => {
      setUploadError(e.message);
    },
  });

  const handleSourceChange = (next: string) => {
    const nextSource = next as UploadSource;
    setSource(nextSource);
    setReport("");
    setUploadError(null);
  };

  const validateAndSetFile = (picked: File | null) => {
    setUploadError(null);
    if (!picked) {
      setFile(null);
      setFileError(null);
      return;
    }
    const ext = extensionOf(picked.name);
    if (!ACCEPTED_UPLOAD_EXTENSIONS.includes(ext as (typeof ACCEPTED_UPLOAD_EXTENSIONS)[number])) {
      setFile(null);
      setFileError("Only .csv, .xlsx, or .xls files are accepted.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFile(picked);
    setFileError(null);
  };

  return (
    <div className="space-y-6">
      {/* ── Upload card ────────────────────────────────────────────── */}
      <section className="rounded-lg border border-border bg-card p-5 sm:p-6">
        <header className="mb-4 flex items-start gap-3">
          <span className="rounded-md bg-primary/10 p-2 text-primary">
            <Upload className="h-4 w-4" aria-hidden />
          </span>
          <div>
            <h2 className="text-base font-semibold text-foreground">Upload a file</h2>
            <p className="text-xs text-muted-foreground">
              Pick a source and report, then choose the file. We validate the schema before storing it.
            </p>
          </div>
        </header>

        <div className={`grid gap-4 ${requiresYear ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
          <FormField label="Source" htmlFor="upload-source">
            <Select value={source} onValueChange={handleSourceChange}>
              <SelectTrigger id="upload-source">
                <SelectValue placeholder="Select source" />
              </SelectTrigger>
              <SelectContent>
                {UPLOAD_SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField label="Report" htmlFor="upload-report">
            <Select
              value={report}
              onValueChange={(v) => setReport(v as UploadReport)}
              disabled={!source}
            >
              <SelectTrigger id="upload-report">
                <SelectValue placeholder={source ? "Select report" : "Pick source first"} />
              </SelectTrigger>
              <SelectContent>
                {availableReports.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          {requiresYear && (
            <FormField label="Year" htmlFor="upload-year">
              <YearPicker
                id="upload-year"
                value={year}
                onChange={setYear}
                minYear={MIN_YEAR}
                maxYear={MAX_YEAR}
              />
            </FormField>
          )}
        </div>

        <div className="mt-5">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_UPLOAD_MIME}
            className="hidden"
            onChange={(e) => validateAndSetFile(e.target.files?.[0] ?? null)}
          />
          <div
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/20 px-4 py-10 text-center transition-colors hover:bg-muted/40"
          >
            <span className="rounded-full bg-background p-3 shadow-sm">
              <Upload className="h-5 w-5 text-muted-foreground" aria-hidden />
            </span>
            <p className="text-sm font-medium text-foreground">
              <span className="text-primary">Click to browse</span> for a file
            </p>
            <p className="text-xs text-muted-foreground">
              CSV, XLSX or XLS · single sheet only
            </p>
          </div>

          {fileError && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5" aria-hidden />
              {fileError}
            </p>
          )}

          {file && (
            <div className="mt-3 flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="truncate text-sm text-foreground">{file.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  · {formatBytes(file.size)}
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground"
                onClick={() => validateAndSetFile(null)}
                aria-label="Remove selected file"
                disabled={uploadMutation.isPending}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {uploadError && (
            <p className="mt-3 flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{uploadError}</span>
            </p>
          )}
        </div>

        <footer className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="outline"
            disabled={!source || !report}
            onClick={() => {
              if (source && report) onOpenLatest?.(source, report);
            }}
          >
            Reference file
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>

          <Button
            type="button"
            disabled={!canUpload || uploadMutation.isPending}
            onClick={() => uploadMutation.mutate()}
          >
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload file
              </>
            )}
          </Button>
        </footer>
      </section>
    </div>
  );
}

function FormField({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function YearPicker({
  id,
  value,
  onChange,
  minYear,
  maxYear,
}: {
  id: string;
  value: string;
  onChange: (year: string) => void;
  minYear: number;
  maxYear: number;
}) {
  const [open, setOpen] = useState(false);
  const selected = Number(value);
  // Newest first — admins almost always pick a recent year, so the current one
  // lands at the top of the grid without scrolling.
  const years = Array.from(
    { length: maxYear - minYear + 1 },
    (_, i) => maxYear - i,
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          )}
        >
          <span>{value || `Select a year`}</span>
          <CalendarIcon className="h-4 w-4 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="max-h-64 overflow-y-auto">
          <div className="grid grid-cols-3 gap-1">
            {years.map((y) => {
              const isSelected = y === selected;
              return (
                <button
                  key={y}
                  type="button"
                  onClick={() => {
                    onChange(String(y));
                    setOpen(false);
                  }}
                  className={cn(
                    "rounded-md px-2 py-1.5 text-sm transition-colors",
                    isSelected
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted",
                  )}
                >
                  {y}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
